import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Placeholder } from '@tiptap/extension-placeholder';
import { BubbleMenu, EditorContent, useEditor } from '@tiptap/react';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { editorProps, extensions, imagesExtensions } from './constants';
import { applyLink } from './helpers';
import { LinkTooltip } from './LinkTooltip';
import { Textarea, TextareaWrapper, Wrapper } from './styled';
import { Toolbar } from './Toolbar';
export const RichTextEditor = forwardRef(function RichTextEditor({ value, onChange, disabled, success, warning, error, readOnly, readOnlyMode, withPreviewToggle, placeholders, linkTooltipPlaceholder = 'Enter a URL', additionalToolbarItems, dataTestId, rows, allowImages, placeholder, ...props }, ref) {
    const [isPreview, setIsPreview] = useState(true);
    const [rawEditorValue, setRawEditorValue] = useState(value ?? '');
    const rawEditorRef = useRef(null);
    const editor = useEditor({
        editable: !readOnly && !disabled,
        extensions: [
            ...extensions,
            ...(allowImages ? imagesExtensions : []),
            Placeholder.configure({
                placeholder,
            }),
        ],
        onBlur: (p) => {
            const html = p.editor.getHTML();
            onChange?.(html);
            setRawEditorValue(html);
        },
        parseOptions: {
            preserveWhitespace: 'full',
        },
        editorProps,
    });
    const handleRawEditorChange = useCallbackRef((e) => {
        const next = e.target.value;
        setRawEditorValue(next);
        onChange?.(next);
    });
    const handleSaveLink = useCallbackRef((href) => {
        if (!editor)
            return;
        applyLink(editor, href);
    });
    const shouldShowLinkTooltip = () => !!editor?.isActive('link');
    useEffect(() => {
        if (editor && value !== editor.getHTML()) {
            editor.commands.setContent(value ?? '', false);
        }
    }, [editor, value]);
    useEffect(() => {
        if (!editor)
            return;
        if (!readOnly && !disabled) {
            editor.setOptions({ editable: true });
        }
        else {
            editor.setOptions({ editable: false });
        }
    }, [editor, readOnly, disabled]);
    useImperativeHandle(ref, () => ({
        focus: () => {
            if (editor) {
                editor.commands.focus();
            }
        },
        select: () => {
            if (editor) {
                editor.commands.focus();
                editor.commands.selectAll();
            }
        },
    }));
    return (_jsxs(Wrapper, { "$inputDisabled": readOnly || disabled, "$readOnlyMode": readOnlyMode, "$success": success, "$warning": warning, "$error": error, rows: rows, "data-testid": dataTestId || 'rich-text-editor', children: [editor && !readOnly && (_jsx(Toolbar, { editor: editor, disabled: disabled || readOnly, placeholders: placeholders, additionalToolbarItems: additionalToolbarItems, isPreview: isPreview, setIsPreview: setIsPreview, withPreviewToggle: withPreviewToggle, rawEditorValue: rawEditorValue, setRawEditorValue: setRawEditorValue, rawEditorRef: rawEditorRef, dataTestId: dataTestId || 'rich-text-editor', allowImages: allowImages, onChange: onChange })), _jsx(EditorContent, { editor: editor, "data-visible": isPreview, "data-testid": `${dataTestId || 'rich-text-editor'}-editor-content`, ...props }), _jsx(TextareaWrapper, { "data-replicated-value": rawEditorValue, "data-visible": !isPreview, children: _jsx(Textarea, { value: rawEditorValue, onChange: handleRawEditorChange, ref: rawEditorRef, "data-testid": `${dataTestId || 'rich-text-editor'}-raw-editor-textarea`, placeholder: placeholder, ...props }) }), editor && !readOnly && !disabled && (_jsx(BubbleMenu, { editor: editor, shouldShow: shouldShowLinkTooltip, children: _jsx(LinkTooltip, { editor: editor, saveLink: handleSaveLink, placeholder: linkTooltipPlaceholder, dataTestId: dataTestId || 'rich-text-editor' }) }))] }));
});
